import { dataDaSlashaDash } from 'app/utility/dataOra';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getQueryGetDto } from '../../ServiceController';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

const collection = "2.16.840.1.113883.6.1";
const collectionRsa = "2.16.840.1.113883.6.103";

const defaultEsame = {
    nomeEsame: '',
    dataEsame: '',
    oraEsame: '',
    codiceEsame: '',
    esito: '',
};

const PrecedentiEsamiForm = ({ initialData = {}, onSubmit, onCancel, precedentiEsami, isRsa }) => {
    const dispatch = useDispatch();
    const useCollection = isRsa ? collectionRsa : collection;

    const processedInitialData = {
        ...defaultEsame,
        ...initialData,
        dataEsame: initialData.dataEsame ? dataDaSlashaDash(initialData.dataEsame) : '',
    };

    const [esame, setEsame] = useState(processedInitialData);
    const [errors, setErrors] = useState({});
    const [mongoResults, setMongoResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState([]);

    // Per il debounce della ricerca
    const searchTimeout = useRef(null);

    // Se sono presenti dati iniziali, pre-seleziono la voce
    useEffect(() => {
        if (initialData && initialData.nomeEsame) {
            setSelected([{
                description: initialData.nomeEsame,
                code: initialData.codiceEsame
            }]);
        }
    }, [initialData]);

    // Funzione per gestire la ricerca con debounce
    const handleSearch = (query) => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = setTimeout(() => {
            if (query.trim() === '') return;
            setIsLoading(true);
            const payload = {
                nomeCollection: useCollection,
                campo: "description",
                valore: query
            };
            dispatch(getQueryGetDto('MongoDb/GetRisultatiMongo', payload))
                .then((results) => {
                    let transformedResults = results && results.length > 0
                        ? results.map(r => r.dati)
                        : [];
                    if (transformedResults.length > 0) transformedResults = transformedResults.sort((a, b) => a.description.localeCompare(b.description));
                    setMongoResults(transformedResults);
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Errore nel servizio MongoDB:', error);
                    setIsLoading(false);
                });
        }, 300);
    };

    // Funzione per aggiornare il campo in base alla selezione
    const handleSelectResult = (selectedItem) => {
        setEsame((prev) => ({
            ...prev,
            // Se si tratta di un'opzione custom, il valore personalizzato si trova in "label"
            nomeEsame: selectedItem.customOption ? selectedItem.label : selectedItem.description,
            codiceEsame: selectedItem.code || prev.codiceEsame,
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!esame.nomeEsame) {
            newErrors.nomeEsame = 'Inserire il nome/descrizione dell’esame.';
        }
        if (!esame.dataEsame) {
            newErrors.dataEsame = 'Inserire la data dell’esame.';
        }
        if (!esame.esito) {
            newErrors.esito = 'Inserire il referto/esito dell’esame.';
        }

        return newErrors;
    };

    // Gestione invio form
    const handleSubmit = (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        onSubmit(esame);
        setEsame({ ...defaultEsame });
        setErrors({});
        setSelected([]);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="form-group">
                <label>Nome/Descrizione dell'Esame</label>
                <AsyncTypeahead
                    id="nomeEsameTypeahead"
                    isLoading={isLoading}
                    onSearch={handleSearch}
                    minLength={2}
                    labelKey="description"
                    options={mongoResults}
                    placeholder="Cerca l'esame..."
                    // Abilita la possibilità di inserire un nuovo testo se non ci sono risultati
                    allowNew
                    newSelectionPrefix="Nuovo: "
                    selected={selected}
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            setSelected(selectedItems);
                            handleSelectResult(selectedItems[0]);
                        } else {
                            setSelected([]);
                        }
                    }}
                    renderMenuItemChildren={(option) => (
                        <span>{option.description} {option.code && `- ${option.code}`}</span>
                    )}
                    className={errors.nomeEsame ? 'is-invalid' : ''}
                />
                {errors.nomeEsame && (
                    <div className="invalid-feedback d-block">{errors.nomeEsame}</div>
                )}
            </div>

            <div className="row">
                <div className="form-group col-md-6">
                    <label>Data</label>
                    <input
                        type="date"
                        name="dataEsame"
                        autoComplete="off"
                        className={`form-control ${errors.dataEsame ? 'is-invalid' : ''}`}
                        value={esame.dataEsame}
                        onChange={(e) => setEsame({ ...esame, dataEsame: e.target.value })}
                    />
                    {errors.dataEsame && (
                        <div className="invalid-feedback">{errors.dataEsame}</div>
                    )}
                </div>
                <div className="form-group col-md-6">
                    <label>Ora (HH:mm) - opzionale</label>
                    <input
                        type="time"
                        name="oraEsame"
                        autoComplete="off"
                        className="form-control"
                        value={esame.oraEsame}
                        onChange={(e) => setEsame({ ...esame, oraEsame: e.target.value })}
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Codice LOINC/ICD (opzionale)</label>
                <input
                    type="text"
                    name="codiceEsame"
                    autoComplete="off"
                    className="form-control"
                    value={esame.codiceEsame}
                    onChange={(e) => setEsame({ ...esame, codiceEsame: e.target.value })}
                />
            </div>

            <div className="form-group">
                <label>Esito/Referto</label>
                <textarea
                    name="esito"
                    autoComplete="off"
                    className={`form-control ${errors.esito ? 'is-invalid' : ''}`}
                    value={esame.esito}
                    onChange={(e) => setEsame({ ...esame, esito: e.target.value })}
                />
                {errors.esito && (
                    <div className="invalid-feedback">{errors.esito}</div>
                )}
            </div>

            <div className="d-flex mt-3">
                <button type="submit" className="btn btn-primary mx-2">
                    Conferma
                </button>
                {precedentiEsami && precedentiEsami.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onCancel}
                    >
                        Annulla
                    </button>
                )}
            </div>
        </form>
    );
};

export default PrecedentiEsamiForm;
